import React, { createContext, useState, useEffect, useContext } from 'react';
import jwt_decode from 'jwt-decode';
import apiService from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set token when it changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      
      try {
        const decoded = jwt_decode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expired
          logout();
        } else {
          loadUser();
        }
      } catch (err) {
        // Invalid token
        logout();
      }
    } else {
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  // Load user
  const loadUser = async () => {
    try {
      const res = await apiService.auth.getProfile();
      setUser(res.data);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading user');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Register user
  const register = async (formData) => {
    try {
      const res = await apiService.auth.register(formData);
      setToken(res.data.token);
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      return { success: false, error: err.response?.data?.message || 'Registration failed' };
    }
  };

  // Login user
  const login = async (formData) => {
    try {
      console.log('Attempting login with:', { email: formData.email });
      const res = await apiService.auth.login(formData);
      console.log('Login response:', res.data);
      if (res.data && res.data.token) {
        setToken(res.data.token);
        return { success: true };
      } else {
        console.error('Login succeeded but no token received');
        setError('Login succeeded but no token received');
        return { success: false, error: 'No token received from server' };
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
      }
      setError(err.response?.data?.message || 'Login failed');
      return { success: false, error: err.response?.data?.message || 'Login failed' };
    }
  };

  // Logout user
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    localStorage.removeItem('token');
  };

  // Clear errors
  const clearError = () => {
    setError(null);
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await apiService.auth.resetPassword(email);
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed');
      return { success: false, error: err.response?.data?.message || 'Password reset failed' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        error,
        register,
        login,
        logout,
        clearError,
        resetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Add useAuth hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};